import { DAWType } from '@/types/daw';
import { DAW_FILE_EXTENSIONS } from '@/common/models/enums';
import { getFileModificationTime } from './filePathUtils';
import type { ElectronAPI } from '@/types/electron';

interface DAWFileWithModTime {
  file: { fileName: string; uploadedAt: string; [key: string]: unknown };
  modTime: number;
  filePath: string;
}

/**
 * Extracts the most recent DAW file from a list of project files
 * Filters for root-level files matching the primary DAW type and sorts by modification time
 * @param files - Array of project files
 * @param primaryDAW - Primary DAW type to filter for
 * @param projectDirPath - Base directory path for the project
 * @param electronAPI - Optional Electron API instance (if in Electron environment)
 * @param isElectron - Whether running in Electron environment
 * @returns Promise resolving to the most recent DAW file with its full path, or null if none found
 */
export async function extractMostRecentDAWFile(
  files: Array<{
    fileName: string;
    uploadedAt: string;
    [key: string]: unknown;
  }>,
  primaryDAW: { type: DAWType },
  projectDirPath: string,
  electronAPI?: ElectronAPI | null,
  isElectron?: boolean
): Promise<{
  file: { fileName: string; uploadedAt: string; [key: string]: unknown };
  filePath: string;
} | null> {
  // Map DAW types to their file extensions
  const dawExtensions: Record<DAWType, string[]> = {
    [DAWType.ABLETON]: [DAW_FILE_EXTENSIONS[0]], // .als
    [DAWType.LOGIC]: [DAW_FILE_EXTENSIONS[1]], // .logicx
    [DAWType.PRO_TOOLS]: [DAW_FILE_EXTENSIONS[2], DAW_FILE_EXTENSIONS[3]], // .ptf, .ptx
    [DAWType.CUBASE]: [DAW_FILE_EXTENSIONS[4]], // .cpr
    [DAWType.FL_STUDIO]: [DAW_FILE_EXTENSIONS[5]], // .flp
    [DAWType.REAPER]: [DAW_FILE_EXTENSIONS[6]], // .rpp
    [DAWType.STEMS]: [],
    [DAWType.UNKNOWN]: [],
  };

  const extensions = dawExtensions[primaryDAW.type];
  if (!extensions || extensions.length === 0) {
    return null;
  }

  // Filter to only files in the root directory (no "/" in fileName)
  const rootFiles = files.filter(
    (file: { fileName: string }) => !file.fileName.includes('/')
  );

  // Filter to only DAW files matching the primary DAW extension
  const dawFiles = rootFiles.filter((file: { fileName: string }) =>
    extensions.some((ext) => file.fileName.toLowerCase().endsWith(ext))
  );

  if (dawFiles.length === 0) {
    return null;
  }

  // Get file modification times from the file system and sort by most recent
  const dawFilesWithModTime: DAWFileWithModTime[] = await Promise.all(
    dawFiles.map(async (file: { fileName: string; uploadedAt: string }) => {
      const filePath = `${projectDirPath}/${file.fileName}`;
      const modTime = await getFileModificationTime(
        filePath,
        file.uploadedAt,
        electronAPI,
        isElectron
      );

      return {
        file,
        modTime:
          typeof modTime === 'number' ? modTime : new Date(modTime).getTime(),
        filePath,
      };
    })
  );

  // Sort by modification time descending (most recent first) and pick the first one
  const mostRecent = dawFilesWithModTime.sort(
    (a, b) => b.modTime - a.modTime
  )[0];

  return {
    file: mostRecent.file,
    filePath: mostRecent.filePath,
  };
}
